import {
  compact,
  find,
  first,
  flatMap,
  intersection,
  isEmpty,
  last,
  map,
  uniq,
} from "lodash/fp";

import { getUniqueKey } from "@app/analytics/utils";
import { SPACE_DELIMITER } from "@app/common/constants/locales";
import {
  TranscriptWord,
  TranscriptTextChunk,
  ReactionHighlight,
  TextChunk,
  TranscriptTurnParagraph,
  WordDelimiter,
} from "@app/common/types";

import { NoteSearchResult, TranscriptSearchResult } from "../types";

const groupHighlightedWords = (
  currentWord: TranscriptWord,
  words: TranscriptWord[],
  wordDelimiter: WordDelimiter,
  currentSearchResult: TranscriptSearchResult,
  nextSearchResult?: TranscriptSearchResult
) => {
  const allWordsInChunk = [];

  // highlight start index is either the start index of the search result (if this is the first word in
  // the highlight), or the start of the word (means the highlight is extending from a previous word)
  const highlightStartIndex =
    currentWord.id === currentSearchResult.match.start.word
      ? currentSearchResult.match.start.index
      : 0;

  // highlight end index is either the end of the search result (if this is the last highlighted word for
  // the result), or the end of the word (means the highlight is extending onto the next word)
  const highlightEndIndex =
    currentWord.id === currentSearchResult.match.end.word
      ? currentSearchResult.match.end.index
      : currentWord.value.length;

  // capture the highlighted fragment and save it in this chunk; don't return
  // yet since there could be words following that need to be included in the
  // chunk as well
  const highlightedWordFragmentValue = currentWord.value.substring(
    highlightStartIndex,
    highlightEndIndex
  );

  allWordsInChunk.push({
    ...currentWord,
    value: highlightedWordFragmentValue,
  });

  let nextWord: TranscriptWord | undefined;
  const searchEndWordId = currentSearchResult.match.end.word;

  if (currentWord.id !== searchEndWordId) {
    nextWord = words.shift();

    // until we find the "end word", we can reasonably assume that
    // any word in-between is highlighted
    while (nextWord && searchEndWordId !== nextWord.id) {
      allWordsInChunk.push(nextWord);
      nextWord = words.shift();
    }

    if (nextWord) {
      // add the highlighted portion of the end word to the chunk
      const highlightedEndWord = {
        ...nextWord,
        value: nextWord.value.substring(0, currentSearchResult.match.end.index),
      };

      allWordsInChunk.push(highlightedEndWord);
    }
  }

  // endWord will be the nextWord (populated in if statement above) if the search
  // result spans across multiple words, otherwise the search starts and ends within
  // a single word, and the end word is the current word
  const endWord = nextWord ?? currentWord;

  if (
    searchEndWordId === endWord.id &&
    currentSearchResult.match.end.index === endWord.value.length
  ) {
    // if the word is highlighted until the end, we need to make sure we're adding
    // a space between this and the next word since words are joined together with
    // " ", adding a blank value back into the turn's words list will do the trick.
    words.unshift({
      ...endWord,
      value: "",
    });
  } else if (nextSearchResult?.match.start.word === endWord.id) {
    // if the word has multiple matching search results, throw it back into the words
    // array to be picked up on the next loop
    words.unshift(endWord);
  } else if (searchEndWordId === endWord.id) {
    // if the next search result matches a different word, we can reasonably assume
    // that the remaining portion of this word is not highlighted. in this case, add
    // only the remaining word fragment back into the words array. the next loop will
    // handle adding the word fragment as an unhighlighted chunk
    const remainingValue = endWord.value.substring(
      currentSearchResult.match.end.index
    );

    if (remainingValue) {
      words.unshift({
        ...endWord,
        value: remainingValue,
      });
    }
  }

  return {
    uniqueKey: getUniqueKey(),
    content: map("value", allWordsInChunk).join(wordDelimiter),
    highlightIndex: currentSearchResult.index,
    highlighted: true,
    words: allWordsInChunk,
  };
};

const groupUnhighlightedWords = (
  currentWord: TranscriptWord,
  words: TranscriptWord[],
  wordDelimiter: WordDelimiter,
  wordsInNextSearchResult: string[],
  nextSearchResult?: TranscriptSearchResult
) => {
  const allWordsInChunk = [currentWord];
  let nextWord: TranscriptWord | undefined = words.shift();
  let addTrailingSpace = false;

  // run through all the words until we reach one that is highlighted
  while (nextWord && !wordsInNextSearchResult.includes(nextWord.id)) {
    allWordsInChunk.push(nextWord);
    nextWord = words.shift();
  }

  if (nextWord) {
    // if there is a next word, that means at least some portion of the next word
    // is highlighted. add the word back to the words array, so it can be picked
    // up in the next loop
    words.unshift(nextWord);

    if (nextSearchResult) {
      const nextSearchResultStartIndex = nextSearchResult.match.start.index;

      if (nextSearchResultStartIndex === 0) {
        // if the start of the next word is highlighted, and the transcript language is space delimited,
        // that means we simply need to append a space at the end of this chunk.
        addTrailingSpace = wordDelimiter === SPACE_DELIMITER;
      } else {
        // if the next result's start index is somewhere in the middle of the word,
        // capture the unhighlighted fragment of the word
        const nextWordUnhighlightedFragment = {
          ...nextWord,
          value: nextWord.value.substring(0, nextSearchResultStartIndex),
        };
        allWordsInChunk.push(nextWordUnhighlightedFragment);
      }
    }
  }

  const combinedContent = map("value", allWordsInChunk).join(wordDelimiter);

  return {
    uniqueKey: getUniqueKey(),
    content: addTrailingSpace ? `${combinedContent} ` : combinedContent,
    highlighted: false,
    words: allWordsInChunk,
  };
};

/**
 * Combines and/or splits up words based on transcript search results and creates content "chunks"
 * @param allWords all words in the chunk (chunks are whole turns or reactions)
 * @param searchResults list of search results to apply to chunk
 *
 * Example scenario:
 *    search term = "bar"
 *    turn words = ["something", "foo", "foobar", "foobarbaz", "other", "word"]
 *
 * Example output (simplified):
 *    content of chunks = ["something foo foo", "bar", " foo", "bar", "baz other word"]
 * */
export const chunkContentBySearchResults = (
  allWords: TranscriptWord[],
  searchResults: TranscriptSearchResult[],
  wordDelimiter: WordDelimiter
): TranscriptTextChunk[] => {
  const chunks: TranscriptTextChunk[] = [];

  if (isEmpty(searchResults) || isEmpty(allWords)) return chunks;

  const words: TranscriptWord[] = [...allWords];

  // index of the current search result of the turn block; may or may not align
  // with the "index" on the search result, since that indicates the index in
  // the entire set of search results, not just this one
  let currentIndexOfSearchResult = 0;
  let currentSearchResult = searchResults[currentIndexOfSearchResult];

  while (!isEmpty(words) && currentSearchResult) {
    const currentWord = words.shift() as TranscriptWord;
    const previousSearchResult = searchResults[currentIndexOfSearchResult - 1];
    const nextSearchResult = searchResults[currentIndexOfSearchResult + 1];

    if (currentSearchResult.words.includes(currentWord.id)) {
      // for everything but the very first word in a chunk, any pre-highlight
      // words/word fragments are captured in the `else` condition of this block. so we need
      // to capture the beginning of the current word as an unhighlighted fragment if all
      // the following are true:
      //   1. the current word is the first word in the chunk
      //   2. the current word intersects with the current search result
      //   3. the search result starts later in the word than the first character
      //   4. the previous run through this loop did not already capture the fragment
      const shouldCaptureUnhighlightedStartWordFragment =
        currentWord.id === first(allWords)?.id &&
        currentWord.id === currentSearchResult.match.start.word &&
        currentSearchResult.match.start.index > 0 &&
        previousSearchResult?.match.start.word !== currentWord.id;

      if (shouldCaptureUnhighlightedStartWordFragment) {
        const unhighlightedFragmentValue = currentWord.value.substring(
          0,
          currentSearchResult.match.start.index
        );

        chunks.push({
          uniqueKey: getUniqueKey(),
          content: unhighlightedFragmentValue,
          highlighted: false,
          words: [{ ...currentWord, value: unhighlightedFragmentValue }],
        });
      }

      const chunk = groupHighlightedWords(
        currentWord,
        words,
        wordDelimiter,
        currentSearchResult,
        nextSearchResult
      );

      chunks.push(chunk);

      if (
        nextSearchResult?.match.start.word ===
        currentSearchResult.match.start.word
      ) {
        // if the next search result starts in the same word that the current search result does,
        // capture any unhighlighted portion between the matches
        const unhighlightedFragmentValue = currentWord.value.substring(
          currentSearchResult.match.end.index,
          nextSearchResult.match.start.index
        );

        if (unhighlightedFragmentValue) {
          chunks.push({
            uniqueKey: getUniqueKey(),
            content: unhighlightedFragmentValue,
            highlighted: false,
            words: [{ ...currentWord, value: unhighlightedFragmentValue }],
          });
        }
      }

      currentIndexOfSearchResult += 1;
      currentSearchResult = searchResults[currentIndexOfSearchResult];
    } else {
      const chunk = groupUnhighlightedWords(
        currentWord,
        words,
        wordDelimiter,
        currentSearchResult.words,
        currentSearchResult
      );

      if (chunk.content) {
        chunks.push(chunk);
      }
    }
  }

  if (!isEmpty(words)) {
    const currentWord = words.shift();

    // if there are no more search results, simply group up the remaining non-highlighted words
    const chunk = groupUnhighlightedWords(
      currentWord!,
      words,
      wordDelimiter,
      [],
      currentSearchResult
    );

    if (chunk.content) {
      chunks.push(chunk);
    }
  }

  return chunks;
};

export const chunkContentByReactions = (
  paragraph: TranscriptTurnParagraph,
  reactions: ReactionHighlight[],
  wordDelimiter: WordDelimiter
): TranscriptTextChunk[] => {
  const chunkedContent: TranscriptTextChunk[] = [];

  const wordAnnotationMap: { [key: string]: ReactionHighlight | null } =
    paragraph.words.reduce((map, word) => ({ ...map, [word.id]: null }), {});

  reactions.forEach((reaction) => {
    const matchingParagraph = find({ id: paragraph.id }, reaction.paragraphs);

    // we should not be receiving duplicate or overlapping significant statements
    // (reactions) from the API, so we can reasonably assume that any single word
    // will only ever have one annotation applied
    for (const word of matchingParagraph!.words) {
      wordAnnotationMap[word.id] = reaction;
    }
  });

  const wordIds = Object.keys(wordAnnotationMap);

  for (let i = 0; i < wordIds.length; i++) {
    const currentWordId = wordIds[i];
    const currentReaction = wordAnnotationMap[currentWordId];
    const currentChunkWordIds = [currentWordId];

    let nextWordIndex = i + 1;
    let nextReaction: ReactionHighlight | null =
      wordAnnotationMap[wordIds[nextWordIndex]];

    while (currentReaction?.id === nextReaction?.id && wordIds[nextWordIndex]) {
      currentChunkWordIds.push(wordIds[nextWordIndex]);
      nextWordIndex += 1;
      nextReaction = wordAnnotationMap[wordIds[nextWordIndex]];
    }

    const reactionWords = paragraph.words.filter((word) =>
      currentChunkWordIds.includes(word.id)
    );

    const currentChunkContent = map("value", reactionWords).join(wordDelimiter);

    if (currentChunkContent) {
      const currentChunk = {
        uniqueKey: getUniqueKey(),
        annotations: compact([currentReaction?.score]),
        content: currentChunkContent,
        highlighted: false,
        tooltipContent: currentReaction?.score,
        words: reactionWords,
      };

      chunkedContent.push(currentChunk);
    }
    i = nextWordIndex - 1;
  }

  return chunkedContent;
};

const isInnerAnnotationOnLeft = (
  currentChunk?: TranscriptTextChunk,
  previousChunk?: TranscriptTextChunk
) => {
  if (!currentChunk || !previousChunk) return false;

  const adjacentChunkOrSubContent = isEmpty(previousChunk.subContent)
    ? previousChunk
    : last(previousChunk.subContent)!;

  return adjacentChunkOrSubContent.highlighted && currentChunk.highlighted;
};

const isInnerAnnotationOnRight = (
  currentChunk?: TranscriptTextChunk,
  nextChunk?: TranscriptTextChunk
) => {
  if (!currentChunk || !nextChunk) return false;

  const adjacentChunkOrSubContent = isEmpty(nextChunk.subContent)
    ? nextChunk
    : nextChunk.subContent![0]!;

  return currentChunk.highlighted && adjacentChunkOrSubContent.highlighted;
};

const withTrailingSpace = (value: string) => {
  return value.endsWith(" ") ? value : `${value} `;
};

const withLeadingSpace = (value: string) => {
  return value.startsWith(" ") ? value : ` ${value}`;
};

const fillInSpaces = (chunks: TranscriptTextChunk[]) => {
  const filledInChunks: TranscriptTextChunk[] = [];

  chunks.forEach((chunk, i) => {
    if (chunk.subContent) {
      const firstSubContentItem = chunk.subContent[0];
      const previousAdjacentChunk = chunks[i - 1];

      const lastSubContentItem = last(chunk.subContent)!;
      const nextAdjacentChunk = chunks[i + 1];

      const isInnerLeft = isInnerAnnotationOnLeft(
        firstSubContentItem,
        previousAdjacentChunk
      );

      const isInnerRight = isInnerAnnotationOnRight(
        lastSubContentItem,
        nextAdjacentChunk
      );

      if (isInnerLeft || isInnerRight) {
        // mutate the first and last items as necessary
        if (isInnerLeft) {
          firstSubContentItem.annotationOptions = {
            ...(firstSubContentItem.annotationOptions || {}),
            innerLeft: true,
          };
        }

        if (isInnerRight) {
          lastSubContentItem.annotationOptions = {
            ...(lastSubContentItem.annotationOptions || {}),
            innerRight: true,
          };

          lastSubContentItem.content = withTrailingSpace(
            lastSubContentItem.content
          );
        }

        filledInChunks.push({
          ...chunk,
          annotationOptions: {
            innerLeft: isInnerLeft,
            innerRight: isInnerRight,
          },
        });

        if (!isInnerRight && !lastSubContentItem.content.endsWith(" ")) {
          // if the highlight continues to the next adjacent chunk, we will handle adding a
          // highlighted space onto the end of the sub-content above. however, if the last
          // item in the subcontent is not highlighted and does not abut another highlighted
          // chunk, we need to add another space here.
          filledInChunks.push({
            content: " ",
            highlighted: false,
            words: [],
            uniqueKey: getUniqueKey(),
          });
        }
        return;
      }
    }

    const isNotAnnotated =
      isEmpty(chunk.annotations) &&
      !chunk.highlighted &&
      isEmpty(chunk.subContent);

    if (isNotAnnotated) {
      // if the current chunk doesn't have any annotations, we can safely add trailing or
      // leading spaces, since this block won't have any styles applied

      // don't add a trailing space if this is the last chunk or if the next chunk is part of the same word
      const nextChunk = chunks[i + 1];
      const shouldAddTrailingSpace =
        nextChunk && first(nextChunk.words)?.id !== last(chunk.words)?.id;
      let contentWithSpaces = shouldAddTrailingSpace
        ? withTrailingSpace(chunk.content)
        : chunk.content;
      const previousChunk = last(filledInChunks);

      // absorb any previous spaces into the unannotated block
      if (previousChunk && previousChunk.content === " ") {
        filledInChunks.pop();
        contentWithSpaces = withLeadingSpace(contentWithSpaces);
      }

      filledInChunks.push({
        ...chunk,
        content: contentWithSpaces,
      });
    } else {
      filledInChunks.push(chunk);

      const nextChunk = chunks[i + 1];
      const hasSplitWord =
        nextChunk && first(nextChunk.words)?.id === last(chunk.words)?.id;

      // add a new chunk instead of simply appending a space onto an existing chunk so that
      // the space doesn't take on the style of the annotation
      if (i < chunks.length - 1 && !hasSplitWord) {
        filledInChunks.push({
          content: " ",
          highlighted: false,
          words: [],
          uniqueKey: getUniqueKey(),
        });
      }
    }
  });

  return filledInChunks;
};

export const chunkParagraphContent = (
  paragraph: TranscriptTurnParagraph,
  wordDelimiter: WordDelimiter,
  reactions: ReactionHighlight[] = [],
  searchResults: TranscriptSearchResult[] = []
): TranscriptTextChunk => {
  // short-circuit this method if we are only applying search highlighting
  if (isEmpty(reactions)) {
    const searchResultChunks = chunkContentBySearchResults(
      paragraph.words,
      searchResults,
      wordDelimiter
    );

    return {
      content: paragraph.text,
      highlighted: false,
      uniqueKey: getUniqueKey(),
      subContent: fillInSpaces(searchResultChunks),
      words: paragraph.words,
    };
  }

  const reactionChunks = chunkContentByReactions(
    paragraph,
    reactions,
    wordDelimiter
  );

  // short-circuit this method if we are only applying reaction annotations
  if (isEmpty(searchResults)) {
    return {
      content: paragraph.text,
      highlighted: false,
      uniqueKey: getUniqueKey(),
      subContent: fillInSpaces(reactionChunks),
      words: paragraph.words,
    };
  }

  const wordsWithHighlights = uniq(flatMap("words", searchResults));

  const chunksAnnotatedByReactionsAndSearchResults = reactionChunks.map(
    (chunk) => {
      const wordsInReaction = map("id", chunk.words);

      // if there are no highlighted word/word fragments in this chunk, then this chunk
      // is fine to be left as-is
      if (isEmpty(intersection(wordsWithHighlights, wordsInReaction))) {
        return chunk;
      }

      const searchResultsForReaction = searchResults.filter(
        (result) => !isEmpty(intersection(result.words, wordsInReaction))
      );

      const reactionChunkedBySearchResult = chunkContentBySearchResults(
        chunk.words,
        searchResultsForReaction,
        wordDelimiter
      );

      const combinedChunks = reactionChunkedBySearchResult.map(
        (searchResultChunk, i) => ({
          ...searchResultChunk,
          annotationOptions: {
            innerLeft: !searchResultChunk.highlighted && i > 0,
            innerRight:
              !searchResultChunk.highlighted &&
              i < reactionChunkedBySearchResult.length - 1,
          },
        })
      );

      return {
        ...chunk,
        subContent: combinedChunks,
      };
    }
  );

  return {
    content: paragraph.text,
    highlighted: false,
    uniqueKey: getUniqueKey(),
    words: paragraph.words,
    subContent: fillInSpaces(chunksAnnotatedByReactionsAndSearchResults),
  };
};

export const chunkNoteBySearchResults = (
  value: string,
  results: NoteSearchResult[]
): TextChunk[] => {
  const firstResult = results[0];
  const chunks = [];

  if (firstResult.match.start.index > 0) {
    // capture first non-highlighted chunk if necessary
    chunks.push({
      content: value.slice(0, firstResult.match.start.index),
      highlighted: false,
      uniqueKey: getUniqueKey(),
    });
  }

  results.forEach((result, i) => {
    // for each result, first capture the highlighted chunk from the result
    chunks.push({
      content: value.slice(result.match.start.index, result.match.end.index),
      highlighted: true,
      highlightIndex: result.index,
      uniqueKey: getUniqueKey(),
    });

    // then capture any un-highlighted text between this and the next highlighted section
    const nextResult = results[i + 1];
    if (nextResult) {
      const unhighlightedChunkContent = value.slice(
        result.match.end.index,
        nextResult.match.start.index
      );

      if (unhighlightedChunkContent) {
        chunks.push({
          content: unhighlightedChunkContent,
          highlighted: false,
          uniqueKey: getUniqueKey(),
        });
      }
    }
  });

  const lastResult = last(results)!;
  if (lastResult.match.end.index < value.length) {
    // capture last non-highlighted chunk if necessary
    chunks.push({
      content: value.slice(lastResult.match.end.index),
      highlighted: false,
      uniqueKey: getUniqueKey(),
    });
  }

  return chunks;
};

export const chunkNoteBySearchResultsAndParagraphs = (
  value: string,
  results: NoteSearchResult[]
): TextChunk[][] => {
  const chunks = chunkNoteBySearchResults(value, results);

  // only go through this complexity if there's more than one paragraph to begin with
  if (value.includes("\n")) {
    const chunksByParagraph: TextChunk[][] = [];
    let chunksForCurrentParagraph: TextChunk[] = [];

    while (chunks.length > 0) {
      const currentChunk = chunks.shift()!;

      if (currentChunk.content.includes("\n")) {
        // -- if the current chunk includes a paragraph break --
        // 1. split the chunk content into segments by paragraph
        // 2. add the "end of paragraph" chunk to the current paragraph chunk list
        // 3. add the next paragraph back into the chunk array; adding it back into the array
        //    will ensure that if more than one paragraph break is included in the remaining chunk
        //    content, it will be split up correctly by a subsequent run through this loop
        const [endParagraphChunkContent, ...rest] = compact(
          currentChunk.content.split(/\n/)
        );

        const endParagraphChunk = {
          ...currentChunk,
          content: endParagraphChunkContent,
          uniqueKey: getUniqueKey(),
        };

        chunksForCurrentParagraph.push(endParagraphChunk);
        chunksByParagraph.push(chunksForCurrentParagraph);
        chunksForCurrentParagraph = [];

        const startParagraphChunk = {
          ...currentChunk,
          content: rest.join("\n"),
        };
        chunks.unshift(startParagraphChunk);
      } else {
        // if the current chunk does not include a paragraph break, simply tack onto the current paragraph's chunks
        chunksForCurrentParagraph.push(currentChunk);
      }
    }

    if (!isEmpty(chunksForCurrentParagraph)) {
      chunksByParagraph.push(chunksForCurrentParagraph);
    }

    return chunksByParagraph;
  }

  return [chunks];
};
